'use client';

import { useSession } from '@shared/auth';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useCheckoutIntent } from './useCheckoutIntent';

type UsePendingCheckoutOptions = {
  onPendingCheckout: (storedData: Record<string, unknown> | null) => void;
};

function usePendingCheckout({ onPendingCheckout }: UsePendingCheckoutOptions) {
  const { session } = useSession();
  const pathname = usePathname();
  const { hasCheckoutIntent, clearCheckoutIntent, getCheckoutData } = useCheckoutIntent();
  const hasTriggeredRef = useRef(false);
  const onPendingCheckoutRef = useRef(onPendingCheckout);

  useEffect(() => {
    onPendingCheckoutRef.current = onPendingCheckout;
  });

  useEffect(() => {
    if (hasTriggeredRef.current) return;
    if (!hasCheckoutIntent(pathname)) return;
    if (session.status === 'uninitialised' || session.status === 'loading') return;

    hasTriggeredRef.current = true;
    const storedData = getCheckoutData();
    clearCheckoutIntent();
    onPendingCheckoutRef.current(storedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status]);

  useEffect(() => {
    return () => {
      clearCheckoutIntent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export { usePendingCheckout };
export type { UsePendingCheckoutOptions };
